import { useEffect, useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { getCachedSession } from '@/lib/sessionCache'
import { VersionDisplay } from '@/components/common/VersionDisplay'
import { Validator } from '@/utils/abacus'
import { logWarn } from '@/lib/log'
import { updateProfileFields, getAvatarSources } from '@/services/userService'

/** Splash ekranı mutlak zaman aşımı — bu süre sonunda /login'e yönlendir */
const SPLASH_TIMEOUT_MS = 15_000
/** "Giriş Ekranına Dön" butonunun görünme süresi */
const ESCAPE_BUTTON_DELAY_MS = 8_000
/** Bounce guard: Bu süre içinde tekrar Home'a dönülürse doğrudan Dashboard'a git */
const BOUNCE_GUARD_MS = 5_000

export default function HomePage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<string>('Yükleniyor...')
  const [showEscape, setShowEscape] = useState(false)
  const abortedRef = useRef(false)

  useEffect(() => {
    // Splash timeout: 15s içinde çözümlenmezse /login'e at
    const splashTimer = setTimeout(() => {
      if (window.location.pathname === '/') {
        logWarn('[Home] ⏰ Splash 15s timeout — /login\'e yönlendiriliyor')
        abortedRef.current = true
        navigate('/login', { replace: true })
      }
    }, SPLASH_TIMEOUT_MS)

    // 8s sonra "Giriş Ekranına Dön" butonu göster
    const escapeTimer = setTimeout(() => {
      setShowEscape(true)
    }, ESCAPE_BUTTON_DELAY_MS)

    checkSystem()

    return () => {
      clearTimeout(splashTimer)
      clearTimeout(escapeTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Kullanıcı escape butonuna tıkladığında */
  const handleEscape = () => {
    abortedRef.current = true
    navigate('/login', { replace: true })
  }

  const checkSystem = async () => {
    try {

      // ─── Bounce Guard: Login ↔ Home döngüsünü kır ───
      const lastCheck = sessionStorage.getItem('home_auth_checked')
      if (lastCheck) {
        const elapsed = Date.now() - parseInt(lastCheck, 10)
        if (elapsed < BOUNCE_GUARD_MS) {
          navigate('/dashboard', { replace: true })
          return
        }
      }

      // 1. Auth Check — Session Cache kullan (tekrar ağ isteği yok)
      setStatus('Kimlik doğrulanıyor...')

      if (abortedRef.current) return

      // ✅ DEV-1 Fix: Developer Bypass (Splash)
      if (typeof window !== 'undefined' && localStorage.getItem('dev-mode-bypass') === 'true') {
        navigate('/dashboard', { replace: true })
        return
      }

      const { session, user } = await getCachedSession()

      if (abortedRef.current) return

      // 💡 ENGELLEME: URL değişmişse navigate etme
      if (window.location.pathname !== '/') {
        return
      }

      if (!user || !session) {
        navigate('/login', { replace: true })
        return
      }

      // ✅ Bounce guard'ı kaydet — başarılı auth kontrolü
      sessionStorage.setItem('home_auth_checked', Date.now().toString())

      // 🔑 Bekleyen davet kodu kontrolü (OAuth sonrası geri dönüş)
      const pendingInviteCode = localStorage.getItem('pending_invite_code')
      if (pendingInviteCode) {
        localStorage.removeItem('pending_invite_code')
        navigate(`/invite/${pendingInviteCode}?returnTo=auto_accept`, { replace: true })
        return
      }

      // ─── JWT & In-Memory Name Bypass (Instant Dashboard) ───
      const expiresAtSec = session.expires_at || 0
      const isTokenValid = expiresAtSec > (Date.now() / 1000) + 60 // En az 1 dakika geçerli token
      const metaName = user.user_metadata?.full_name || user.user_metadata?.name

      if (isTokenValid && metaName && metaName.trim().length >= 2) {
        if (window.location.pathname === '/') {
          navigate('/dashboard', { replace: true })
        }
        return
      }

      // 2. Profil kontrolü — Avatar + Profile TEK sorguda birleştirildi
      setStatus('Profil kontrol ediliyor...')

      let shouldGoToDashboard = true

      try {
        const { data: profile } = await getAvatarSources(user.id)

        if (window.location.pathname !== '/' || abortedRef.current) return

        if (!profile?.full_name || profile.full_name.trim() === '') {
          // 🔄 OAuth Fallback: user_metadata'dan isim kurtarmayı dene
          const metaName = user.user_metadata?.full_name
            || user.user_metadata?.name
            || null

          if (metaName) {
            const sanitized = Validator.sanitize(metaName.trim())
            if (sanitized && sanitized.length >= 2) {
              // Name update'i arka planda yap, kullanıcıyı bekleme
              void updateProfileFields(user.id, { full_name: sanitized })
              if (window.location.pathname === '/') navigate('/dashboard', { replace: true })
              return
            }
          }

          // İsim ne DB'de ne metadata'da var
          const authProvider = user.app_metadata?.provider
          const isSocialLogin = authProvider === 'apple' || authProvider === 'google'

          if (isSocialLogin) {
            // 🍎 Apple HIG: Social login kullanıcılarını Onboarding'e yönlendirme
            shouldGoToDashboard = true
          } else {
            // Email/şifre ile kayıt → Onboarding gerekli
            if (window.location.pathname === '/') navigate('/onboarding', { replace: true })
            return
          }
        }

        // 3. Avatar Sync — Lazy Background (requestIdleCallback)
        const provider = user.app_metadata?.provider
        const socialAvatar = user.user_metadata?.avatar_url
        if (socialAvatar && profile) {
          const needsUpdate =
            (provider === 'google' && profile.google_avatar_url !== socialAvatar) ||
            (provider === 'apple' && profile.apple_avatar_url !== socialAvatar)

          if (needsUpdate) {
            const updateData: Record<string, string> = {}
            if (provider === 'google') updateData.google_avatar_url = socialAvatar
            else if (provider === 'apple') updateData.apple_avatar_url = socialAvatar

            const doAvatarSync = () => {
              // Avatar eşitlemesi kritik değil; hata servis içinde günlüğe yazılır.
              void updateProfileFields(user.id, updateData)
            }

            if ('requestIdleCallback' in window) {
              requestIdleCallback(doAvatarSync)
            } else {
              setTimeout(doAvatarSync, 2000)
            }
          }
        }
      } catch (profileError) {
        // ─── PGRST002 Toleransı: Session geçerli ama DB sorgusu başarısız ───
        // Kullanıcı var (auth başarılı) ama profil/avatar sorgusu hata verdi (PGRST002, 503, vb.)
        // Bu durumda /login'e GÖNDERMEYİZ → doğrudan Dashboard'a git
        // Dashboard kendi getDashboardData() ile profil bilgilerini zaten çekecek
        logWarn('[Home] ⚠️ Profil sorgusu başarısız ama session geçerli → Dashboard\'a yönlendiriliyor', profileError)
        shouldGoToDashboard = true
      }

      if (shouldGoToDashboard && window.location.pathname === '/') {
        navigate('/dashboard', { replace: true })
      }

    } catch {
      // ─── Genel hata: Session bile alınamadıysa login'e yönlendir ───
      if (window.location.pathname === '/' && !abortedRef.current) navigate('/login', { replace: true })
    }
  }

  return (
    <div className="min-h-screen bg-white p-4 font-sans relative overflow-hidden flex flex-col items-center justify-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center space-y-6"
      >
        {/* Brand Logo - Updated to use Asset */}
        <div className="flex flex-col items-center mb-6">
          <div className="relative w-72 h-72">
            <img
              src="/Gunum-Var-App-Splash-Logo-288-288.png"
              alt="Günüm Var!"
              className="object-contain w-full h-full"
            />
          </div>
        </div>

        {/* Status & Loader */}
        <div className="h-6">
          <p className="text-sm text-[var(--color-primary)] animate-pulse font-medium">{status}</p>
        </div>
      </motion.div>

      {/* Escape Button — 8s sonra görünür */}
      <AnimatePresence>
        {showEscape && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-24"
          >
            <button
              onClick={handleEscape}
              className="px-6 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors active:scale-95"
            >
              Giriş Ekranına Dön
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Version */}
      <div className="absolute bottom-8 opacity-50">
        <VersionDisplay />
      </div>
    </div>
  )
}

